import { Divider, IconButton, Stack, Typography } from '@mui/material';

import { Icon } from '@karehero/llama/components/atoms/Icon/Icon';

import { say } from '@/james-playground/aa-family-claim/claim/asks';
import { type Row } from '@/james-playground/aa-family-claim/claim/facts';
import { rowsOf } from '@/james-playground/aa-family-claim/claim/registry';
import {
  ACTIVITIES,
  MANAGES,
  NEEDED_HINT,
  TABLE,
} from '@/james-playground/aa-family-claim/claim/cards/cards.content';
import {
  AddButton,
  CardShell,
  gateQuestion,
  Field,
  Parts,
  Toggle,
  useDraft,
  useQids,
} from '@/james-playground/aa-family-claim/claim/cards/parts';
import { type CardProps } from '@/james-playground/aa-family-claim/claim/cards/types';
import {
  cleanRows,
  columnIds,
  rows as rowsIn,
  str,
} from '@/james-playground/aa-family-claim/claim/cards/values';

const TABLES = [
  {
    id: 'homeActivities',
    heading: ACTIVITIES.home,
    example: ACTIVITIES.homeExample,
  },
  {
    id: 'outsideActivities',
    heading: ACTIVITIES.outside,
    example: ACTIVITIES.outsideExample,
  },
];

/**
 * Q39: what she does, or would like to do, and the help she needs from
 * someone to do it, at home and out and about. Two small tables of up to
 * three rows each; rows are keyed by the form's column ids, which for Q39 are
 * its first row of cells.
 */
const ActivitiesCard = ({
  ask,
  facts,
  voice: v,
  onSubmit,
  onSkip,
}: CardProps) => {
  const { draft, set, visible } = useDraft(ask, facts);
  const qid = useQids();
  const gateId = ask.gate ?? '';
  const gate = str(draft[gateId]);
  const tables = TABLES.filter(
    (t) => ask.entries.includes(t.id) && visible(t.id),
  );

  const rowsFor = (t: string): Row[] => {
    const r = rowsIn(draft[t]);
    return r.length ? r : [{}];
  };
  const columns = (t: string) => columnIds(t);
  const setCell = (t: string, i: number, c: string, value: string) =>
    set(
      t,
      rowsFor(t).map((row, j) => (j === i ? { ...row, [c]: value } : row)),
    );

  const cleaned = (t: string) => cleanRows(rowsFor(t), columns(t));
  const ready =
    gate === 'No' ||
    (gate === 'Yes' && tables.some((t) => cleaned(t.id).length > 0));

  const submit = () => {
    if (gate !== 'Yes') return onSubmit({ [gateId]: 'No' }, say(MANAGES, v));
    const values = {
      [gateId]: 'Yes',
      ...Object.fromEntries(tables.map((t) => [t.id, cleaned(t.id)])),
    };
    const names = tables.flatMap((t) =>
      cleaned(t.id)
        .map((row) => row[columns(t.id)[0]])
        .filter(Boolean),
    );
    onSubmit(values, names.join(', '));
  };

  const labels = [
    say(ACTIVITIES.columns.what, v),
    say(ACTIVITIES.columns.help, v),
    ACTIVITIES.columns.often,
  ];

  return (
    <CardShell
      label={say(ask.title, v)}
      ready={ready}
      onContinue={submit}
      onSkip={onSkip}
    >
      <Toggle
        {...gateQuestion(ask, v)}
        hint={say(NEEDED_HINT, v)}
        value={gate}
        onChange={(value) => set(gateId, value)}
      />
      {gate === 'Yes' && (
        <Parts>
          {tables.map((t) => {
            const tableRows = rowsFor(t.id);
            const limit = rowsOf(t.id);
            return (
              <Stack
                key={t.id}
                spacing={1.5}
                role='group'
                aria-labelledby={qid(t.id)}
              >
                <Typography id={qid(t.id)} variant='h6'>
                  {t.heading}
                </Typography>
                <Stack spacing={2} divider={<Divider flexItem />}>
                  {tableRows.map((row, i) => (
                    <Stack
                      key={i}
                      spacing={1.25}
                      role='group'
                      aria-label={`${t.heading}, ${ACTIVITIES.row(i + 1)}`}
                    >
                      {tableRows.length > 1 && (
                        <Stack
                          sx={{
                            alignItems: 'center',
                            justifyContent: 'space-between',
                          }}
                          direction='row'
                        >
                          <Typography variant='body2'>
                            {ACTIVITIES.row(i + 1)}
                          </Typography>
                          <IconButton
                            aria-label={`${ACTIVITIES.removeRow(i + 1)}, ${t.heading.toLowerCase()}`}
                            onClick={() =>
                              set(
                                t.id,
                                tableRows.filter((_, j) => j !== i),
                              )
                            }
                          >
                            <Icon icon='trash' fontSize={16} />
                          </IconButton>
                        </Stack>
                      )}
                      {columns(t.id).map((c, k) => (
                        <Field
                          key={c}
                          multiline={k === 1}
                          label={labels[k] ?? c}
                          helperText={
                            k === 0 && i === 0 ? t.example : undefined
                          }
                          value={row[c] ?? ''}
                          onChange={(e) => setCell(t.id, i, c, e.target.value)}
                        />
                      ))}
                    </Stack>
                  ))}
                </Stack>
                {tableRows.length < limit ? (
                  <AddButton
                    label={`${ACTIVITIES.add} ${t.heading.toLowerCase()}`}
                    onClick={() => set(t.id, [...tableRows, {}])}
                  />
                ) : (
                  <Typography variant='body2'>{TABLE.full(limit)}</Typography>
                )}
              </Stack>
            );
          })}
        </Parts>
      )}
    </CardShell>
  );
};

ActivitiesCard.displayName = 'ActivitiesCard';
export default ActivitiesCard;
